import React, { ReactElement, useEffect, useState } from "react";

import Divider from "@mui/material/Divider";

import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import BasicSelector from "../../../../components/Redux/BasicSelector";
import OptionalSwitch from "../../../../components/Redux/OptionalSwitch";
import StringMapInput from "../../../../components/Redux/StringMapInput";
import Switch from "../../../../components/Redux/Switch";
import SettingSection from "../../../../components/SettingSection";
import TabSection from "../../../../components/styled/TabSection";
import useFeatureFlag from "../../../../hooks/use-feature-flag";
import usePrevious from "../../../../hooks/use-previous";
import {
  getRouteFormProperty,
  setRouteFormError,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import { BearerTokenFormatOptions } from "../../../settings/components/BearerTokenFormatOptions";
import HostHeaderSetting from "../HostHeaderSetting";
import RewriteHeaders from "../fields/RewriteHeaders";
import { SectionProps } from "./common";

export const Headers = ({ readonly, tabValue }: SectionProps): ReactElement => {
  const dispatch = useDispatch();
  const hostHeaders = useSelector(
    getRouteFormProperty("host_headers", ""),
  ) as string;
  const removeRequestHeaders = useSelector(
    (state) => state.routes.form?.removeRequestHeaders,
  );
  const [headers, setHeaders] = useState(hostHeaders);
  const prevHeaders = usePrevious(headers);
  const idpTokensEnabled = useFeatureFlag("idp-tokens", false);

  useEffect(() => {
    if (prevHeaders) {
      //clear the property that we switched from
      dispatch(setRouteFormProperty({ path: prevHeaders, value: undefined }));
      if (prevHeaders === "host_path_regex_rewrite_pattern") {
        dispatch(
          setRouteFormProperty({
            path: "host_path_regex_rewrite_substitution",
            value: undefined,
          }),
        );
      }
    }
  }, [dispatch, prevHeaders]);

  useEffect(() => {
    setHeaders(hostHeaders);
  }, [hostHeaders]);

  return (
    <TabSection index={"headers"} value={tabValue} title="Headers">
      <SettingSection
        label="Pass Identity Headers"
        docsKey="pass-identity-headers-per-route"
      >
        <OptionalSwitch
          path="passIdentityHeaders"
          label=""
          disabled={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
      <Divider />

      <SettingSection
        label="Google Cloud Serverless Authentication"
        docsKey="enable-google-cloud-serverless-authentication"
      >
        <Switch
          path="enableGoogleCloudServerlessAuthentication"
          label=""
          disabled={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>

      <Divider />

      <HostHeaderSetting readonly={false} />

      <Divider />

      <SettingSection label="Set Request Headers" docsKey="set-request-headers">
        <StringMapInput
          readonly={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          path={"setRequestHeaders"}
        />
      </SettingSection>

      <Divider />

      <SettingSection
        label="Remove Request Headers"
        docsKey="remove-request-headers"
      >
        <MultiValueTextFieldGroup
          disabled={readonly}
          values={removeRequestHeaders || []}
          onSave={(values) =>
            dispatch(
              setRouteFormProperty({
                path: "removeRequestHeaders",
                value: values.length === 0 ? undefined : values,
              }),
            )
          }
        />
      </SettingSection>

      <Divider />

      <SettingSection
        label="Rewrite Response Headers"
        docsKey="rewrite-response-headers"
      >
        <RewriteHeaders readonly={readonly} />
      </SettingSection>

      <Divider />

      <SettingSection
        label="Set Response Headers"
        docsKey="set-response-headers-route"
      >
        <StringMapInput
          readonly={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          path={"setResponseHeaders"}
        />
      </SettingSection>

      {idpTokensEnabled && (
        <>
          <Divider />
          <SettingSection
            label="Bearer Token Format"
            docsKey="bearer-token-format"
          >
            <BasicSelector
              path="bearerTokenFormat"
              options={BearerTokenFormatOptions}
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              readonly={readonly}
            />
          </SettingSection>
        </>
      )}
    </TabSection>
  );
};
